<template>
  <v-page-content>
    <h3><em class="fas fa-1x fa-book-open"></em> Журналы активности</h3>
    <b-card no-body footer-tag="footer" style="min-width: 250px">
      <b-tabs card>
        <b-tab
          v-for="key in Object.keys(types)"
          :title="types[key]"
          :key="key"
          :active="active === key"
          @click="changeTab(key)"
        />
      </b-tabs>
      <div class="p-2">
        <div class="logs-table" ref="gridDiv">
          <ag-grid-vue
            style="height: 100%;"
            class="ag-theme-material custom-table"
            :gridOptions="gridOptions"
            :columnDefs="columnDefs"
            :rowData="logs"
            :pagination="true"
            :suppressPaginationPanel="true"
            @filterChanged="onFilterChanged"
            @grid-ready="onGridReady"
            @rowClicked="onRowClicked"
            @gridSizeChanged="onGridSizeChanged"
            :frameworkComponents="frameworkComponents"
          />
        </div>
        <div class="mt-2 text-right">
          <paginator
            :pages="pages"
            :currentPage="currentPage"
            :pageSize="paginationPageSize"
            :total="total"
            :itemsCount="logs.length"
            @toFirstPage="onToFirstPage"
            @toLastPage="onToLastPage"
            @previousPage="onPreviousPage"
            @nextPage="onNextPage"
          />
        </div>
      </div>
    </b-card>
    <LogInfoModal
      :log-data="selectedLogData"
    />
  </v-page-content>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue'
import _ from 'lodash'
import moment from 'moment'
import Paginator from '@/components/helpers/Paginator.vue'
import AG_GRID_LOCALE_RU from '@/config/locale.ru'
import RoleRenderer from '@/components/helpers/renderers/RoleRenderer'
import PartialMatchFilter from '@/components/helpers/filters/PartialMatchFilter'
import logsApi from '@/api/logs'
import LogInfoModal from '@/components/admin/modals/users/LogInfoModal.vue'

export default {
  name: 'ActivityLogs',
  components: {
    LogInfoModal,
    'ag-grid-vue': AgGridVue,
    Paginator
  },
  data() {
    return {
      types: {
        users: 'Пользователи',
        content: 'Контент',
        organizations: 'Организации',
        gpo: 'ГПО',
        auth: 'Авторизация'
      },
      selectedLogData: null,
      active: null,
      pages: 1,
      currentPage: 1,
      total: 0,
      paginationPageSize: 20,
      logs: [],
      rowData: null,
      gridApi: null,
      gridOptions: {
        defaultColDef: {
          cellStyle: {
            'white-space': 'nowrap'
          },
          suppressSizeToFit: true,
          sortable: false,
          resizable: true,
          autoHeight: false,
          filter: 'partialMatchFilter',
          headerClass: 'ag-grid-table-header'
        },
        localeText: AG_GRID_LOCALE_RU,
        headerHeight: 40,
        enableCellTextSelection: true,
        suppressMenuHide: true
      },
      frameworkComponents: {
        roleRenderer: RoleRenderer,
        partialMatchFilter: PartialMatchFilter
      },
      columnDefs: [
        {
          headerName: 'Пользователь',
          field: 'user',
          width: 250
        },
        {
          headerName: 'Роль',
          field: 'roles',
          filter: false,
          cellRenderer: 'roleRenderer',
          width: 110
        },
        {
          headerName: 'Дата',
          field: 'createdAt',
          filter: 'agDateColumnFilter',
          filterParams: {
            suppressAndOrCondition: true,
            filterOptions: ['equals'],
            closeOnApply: true,
            newRowsAction: 'keep'
          },
          width: 150
        },
        {
          headerName: 'Описание',
          suppressSizeToFit: false,
          field: 'description',
          minWidth: 300
        }
      ]
    }
  },
  created() {
    this.active = Object.keys(this.types)[0]
    this.debouncedFetchFilteredData = _.debounce(() => {
      this.currentPage = 1
      this.fetchLogs()
    }, 500)
    this.debouncedFetchFilteredData()
  },
  methods: {
    changeTab(tab) {
      this.active = tab
      this.debouncedFetchFilteredData()
    },
    async fetchLogs() {
      const response = await logsApi.getActivityLogs(this.getFilters(), this.currentPage, this.paginationPageSize)
      this.pages = response.data.pages
      this.total = response.data.total
      this.logs = response.data.docs.map((log) => ({
        data: log,
        user: log.user.email,
        roles: log.user.roles,
        description: log.description,
        createdAt: moment(log.createdAt).format('DD.MM.YYYY HH:mm')
      }))
    },
    onGridReady(params) {
      this.gridApi = params.api
      this.onGridSizeChanged()
    },
    onGridSizeChanged() {
      if (this.$refs.gridDiv.clientWidth >= 600) {
        this.gridApi.sizeColumnsToFit()
      }
    },
    onRowClicked(row) {
      this.$bvModal.show('logInfoModal')
      this.selectedLogData = row.data.data
    },
    onFilterChanged() {
      this.debouncedFetchFilteredData()
    },
    getFilters() {
      if (this.gridApi) {
        const filterModel = _.cloneDeep(this.gridApi.getFilterModel())
        const filters = Object.entries(filterModel)
          .map(([key, value]) => ({
            param: key,
            needle: _.hasIn(value, 'value') ? value.value : value.dateFrom,
            isStrict: false
          }))
        filters.push({
          param: 'type',
          needle: this.active,
          isStrict: false
        })
        return filters
      }
      return []
    },
    onPreviousPage() {
      if (this.currentPage > 1) {
        this.currentPage -= 1
        this.fetchLogs()
      }
    },
    onNextPage() {
      if (this.currentPage < this.pages) {
        this.currentPage += 1
        this.fetchLogs()
      }
    },
    onToFirstPage() {
      this.currentPage = 1
      this.fetchLogs()
    },
    onToLastPage() {
      if (this.currentPage < this.pages) {
        this.currentPage = this.pages
        this.fetchLogs()
      }
    }
  }
}
</script>

<style scoped>
.logs-table {
  height: calc(100vh - 320px);
  border-bottom: 1px solid lightgray;
}
</style>
